import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { User, TrendingUp } from "lucide-react";

interface Athlete {
  id: string;
  name: string;
  sport: string;
  position?: string;
  year?: string;
  jersey_number?: string;
  active_deals_count: number;
  total_deals_value: number;
}

interface AthleteCardProps {
  athlete: Athlete;
}

export const AthleteCard = ({ athlete }: AthleteCardProps) => {
  const navigate = useNavigate();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Card
      className="cursor-pointer hover:shadow-lg transition-shadow"
      onClick={() => navigate(`/dashboard/athletes/${athlete.id}`)}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">{athlete.name}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {athlete.sport} {athlete.position && `• ${athlete.position}`}
              </p>
            </div>
          </div>
          {athlete.jersey_number && (
            <Badge variant="outline">#{athlete.jersey_number}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Year</span>
            <span className="font-medium capitalize">{athlete.year || "N/A"}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Active Deals</span>
            <span className="font-medium">{athlete.active_deals_count}</span>
          </div>
          <div className="flex justify-between text-sm items-center">
            <span className="text-muted-foreground">Total Value</span>
            <span className="font-medium flex items-center gap-1">
              <TrendingUp className="h-3 w-3 text-green-600" />
              {formatCurrency(athlete.total_deals_value)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};